import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    league: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    password: '',
    confirmPassword: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...registerData } = formData;
      await register(registerData);
      toast.success('Club registrado correctamente');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar el club');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex" data-testid="register-page">
      {/* Left side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/3621104/pexels-photo-3621104.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-l from-obsidian via-obsidian/50 to-transparent" />
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 py-12">
        <div className="max-w-md mx-auto w-full">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>Volver</span>
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <span className="font-heading font-bold text-xl text-white">SC</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-white tracking-tight">Solo Cracks</h1>
              <span className="text-xs text-slate-400 uppercase tracking-widest">Connect</span>
            </div>
          </div>

          <h2 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-2">
            Registrar Club
          </h2>
          <p className="text-slate-400 mb-8">
            Únete a nuestra red de clubes de fútbol femenino.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name" className="text-slate-300 font-heading uppercase tracking-wider text-xs">
                  Nombre del Club *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
                  placeholder="FC Barcelona Femení"
                  required
                  data-testid="register-name"
                />
              </div>

              <div>
                <Label htmlFor="country" className="text-slate-300 font-heading uppercase tracking-wider text-xs">
                  País *
                </Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="mt-2 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
                  placeholder="España"
                  required
                  data-testid="register-country"
                />
              </div>

              <div>
                <Label htmlFor="league" className="text-slate-300 font-heading uppercase tracking-wider text-xs">
                  Liga *
                </Label>
                <Input
                  id="league"
                  value={formData.league}
                  onChange={(e) => setFormData({ ...formData, league: e.target.value })}
                  className="mt-2 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
                  placeholder="Liga F"
                  required
                  data-testid="register-league"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="contact_name" className="text-slate-300 font-heading uppercase tracking-wider text-xs">
                  Persona de Contacto *
                </Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  className="mt-2 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
                  placeholder="Nombre completo"
                  required
                  data-testid="register-contact-name"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="contact_email" className="text-slate-300 font-heading uppercase tracking-wider text-xs">
                  Email de Contacto *
                </Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="mt-2 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
                  placeholder="contacto@club.com"
                  required
                  data-testid="register-email"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="contact_phone" className="text-slate-300 font-heading uppercase tracking-wider text-xs">
                  Teléfono (Opcional)
                </Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="mt-2 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
                  placeholder="+34 600 000 000"
                  data-testid="register-phone"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-300 font-heading uppercase tracking-wider text-xs">
                  Contraseña *
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-2 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
                  placeholder="••••••••"
                  required
                  data-testid="register-password"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-slate-300 font-heading uppercase tracking-wider text-xs">
                  Confirmar *
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="mt-2 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
                  placeholder="••••••••"
                  required
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-6 font-heading uppercase tracking-wide mt-6"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Registrando...
                </>
              ) : (
                'Registrar Club'
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-slate-400">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-indigo-400 hover:text-indigo-300 font-medium">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
